import { useState, useCallback, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { MagnifyingGlass, Robot, CheckCircle, Fire, ArrowUpRight } from "@phosphor-icons/react";
import SEO from "@/components/SEO";
import AINavBar from "@/components/AINavBar";
import { useInfiniteFeed } from "@/hooks/useInfiniteFeed";
import { fetchAISection } from "@/lib/api";
import { stripHtml } from "@/lib/stripHtml";

const CATEGORIES = ["All", "Coding Agent", "Web Agent", "Computer Agent", "Agent Framework"];

function SkeletonCard() {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 animate-pulse">
      <div className="flex gap-3 mb-3">
        <div className="w-10 h-10 bg-slate-200 rounded-xl shrink-0" />
        <div className="flex-1">
          <div className="h-4 bg-slate-200 rounded w-1/2 mb-2" />
          <div className="h-3 bg-slate-200 rounded w-1/3" />
        </div>
      </div>
      <div className="h-3 bg-slate-200 rounded w-full mb-2" />
      <div className="h-3 bg-slate-200 rounded w-3/4" />
    </div>
  );
}

function AgentCard({ agent }) {
  const slug = agent.slug || agent.id;
  return (
    <Link to={`/trending-ai/agents/${slug}`} className="block bg-white border border-slate-200 rounded-2xl p-5 hover:border-blue-300 hover:shadow-md transition-all group">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center shrink-0">
            <Robot size={20} className="text-blue-600" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-base font-bold text-slate-900 group-hover:text-blue-600 transition-colors">{stripHtml(agent.name)}</h3>
              {(agent.isNew || agent.is_new) && <span className="text-[10px] font-bold bg-emerald-500 text-white px-1.5 py-0.5 rounded">NEW</span>}
              {agent.hot && (
                <span className="text-[10px] font-bold bg-red-500 text-white px-1.5 py-0.5 rounded flex items-center gap-0.5">
                  <Fire size={7} weight="fill" /> HOT
                </span>
              )}
            </div>
            <p className="text-xs text-slate-500">{agent.by || agent.creator} · {agent.category}</p>
          </div>
        </div>
        {agent.score && (
          <div className="flex flex-col items-end gap-1">
            <div className="text-lg font-black text-blue-600">{agent.score}</div>
            <div className="text-[10px] text-slate-400">score</div>
          </div>
        )}
      </div>

      <p className="text-sm text-slate-600 leading-relaxed mb-4">{stripHtml(agent.description)}</p>

      {agent.capabilities && agent.capabilities.length > 0 && (
        <div className="mb-4">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Capabilities</p>
          <div className="flex flex-wrap gap-1.5">
            {agent.capabilities.map((cap) => (
              <span key={cap} className="flex items-center gap-1 text-[10px] font-medium bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                <CheckCircle size={8} className="text-emerald-500" weight="fill" /> {cap}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between border-t border-slate-100 pt-3">
        <span className={`text-xs font-semibold px-2 py-1 rounded-lg ${
          agent.openSource ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"
        }`}>
          {agent.openSource ? "Open Source" : "Proprietary"}
        </span>
        <span className="text-xs font-medium text-slate-600 bg-slate-100 px-2 py-1 rounded-lg">{agent.pricing}</span>
        {(agent.website || agent.url) && (
          <span className="flex items-center gap-1 text-xs font-medium text-blue-600">
            Visit site <ArrowUpRight size={11} />
          </span>
        )}
      </div>
    </Link>
  );
}

export default function AIAgentsPage() {
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("All");

  const fetchFn = useCallback(
    (page) => fetchAISection("agents", { page, page_size: 12 }),
    []
  );
  const { items: apiItems, loading, loadingMore, hasMore, error, loadMore } = useInfiniteFeed({
    fetchFn,
    feedKey: "ai-agents",
    getCacheKey: (page) => `ai-agents-p${page}`,
    ttlMs: 10 * 60 * 1000,
  });

  const sentinelRef = useRef(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) loadMore(); }, { rootMargin: "300px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [loadMore]);

  const allItems = apiItems;

  const filtered = allItems
    .filter((a) => {
      const matchCat = cat === "All" || a.category === cat;
      const matchSearch = !search || a.name.toLowerCase().includes(search.toLowerCase()) || (a.description || "").toLowerCase().includes(search.toLowerCase());
      return matchCat && matchSearch;
    })
    .sort((a, b) => (b.score || 0) - (a.score || 0));

  return (
    <>
      <SEO
        title="AI Agents — Autonomous AI Agent Directory · TrendMing"
        description="Discover AI agents: coding agents, web agents, computer-use agents, and frameworks. Track Devin, Operator, AutoGen, LangGraph, CrewAI and more."
        canonical="https://trendming.com/trending-ai/agents"
      />
      <AINavBar />
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">
        <div className="mb-6">
          <span className="text-xs font-semibold uppercase tracking-wider text-blue-600">AI Intelligence · Agents</span>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mt-1">AI Agent Hub</h1>
          <p className="text-slate-500 text-sm mt-1">Track autonomous AI agents, agent frameworks, and agentic systems shaping the future of AI.</p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative max-w-sm flex-1">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search agents…"
              className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  cat === c ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-4 mb-5 text-xs text-slate-500">
          <span>{filtered.length} agents</span>
          <span>·</span>
          <span>{allItems.filter((a) => a.openSource).length} open source</span>
          <span>·</span>
          <span>{allItems.filter((a) => a.isNew || a.is_new).length} new this month</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {loading && Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
          {!loading && filtered.map((agent) => <AgentCard key={agent.id || agent.slug} agent={agent} />)}
          {!loading && filtered.length === 0 && (
            <div className="col-span-full text-center py-16 text-slate-400">
              <p className="text-lg font-medium">No agents found</p>
              <p className="text-sm mt-1">Try adjusting your filters</p>
            </div>
          )}
        </div>

        {hasMore && !loading && <div ref={sentinelRef} className="h-4" />}
        {loadingMore && <div className="text-center py-4 text-xs text-slate-400">Loading more…</div>}
        {error && !loading && (
          <div className="text-center py-8 text-slate-500">
            <p className="text-red-500 font-medium">Could not load agents</p>
            <p className="text-xs mt-1">Content is being refreshed — check back shortly.</p>
          </div>
        )}

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { icon: "👾", title: "Coding Agents", desc: "Autonomous agents that plan, write, test, and deploy code end-to-end.", link: "/trending-ai/coding" },
            { icon: "🌐", title: "Web Agents", desc: "Agents that browse, extract data, fill forms, and complete tasks on the web.", link: "/trending-ai/agents" },
            { icon: "🔧", title: "Agent Frameworks", desc: "Open-source frameworks for building custom multi-agent systems.", link: "/trending-ai/agents" },
          ].map((item) => (
            <Link key={item.title} to={item.link} className="bg-slate-50 border border-slate-200 rounded-xl p-4 hover:border-blue-300 transition-colors group">
              <div className="text-2xl mb-2">{item.icon}</div>
              <h3 className="text-sm font-semibold text-slate-800 group-hover:text-blue-600 mb-1 transition-colors">{item.title}</h3>
              <p className="text-xs text-slate-500 leading-relaxed">{item.desc}</p>
            </Link>
          ))}
        </div>
      </div>
    </>
  );
}
